import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiChevronDown, FiPhone, FiMail, FiGlobe } from 'react-icons/fi';
import { useLanguage } from '../../contexts/LanguageContext';

const megaMenuItems = {
  Courses: {
    sections: [
      {
        title: 'Medical Programs',
        icon: '🏥',
        links: [
          { label: 'MBBS', to: '/courses/mbbs-india' },
          { label: 'BDS – Dental Surgery', to: '/courses/bds' },
          { label: 'BAMS – Ayurvedic Medicine', to: '/courses/bams' },
          { label: 'BHMS – Homeopathy', to: '/courses/bhms' },
          { label: 'BPT – Physiotherapy', to: '/courses/bpt' },
          { label: 'MD / MS (PG Medical)', to: '/courses/md-ms' },
        ],
      },
      {
        title: 'Engineering & IT',
        icon: '⚙️',
        links: [
          { label: 'B.Tech / B.E.', to: '/courses/btech' },
          { label: 'M.Tech Programs', to: '/courses/mtech' },
          { label: 'BCA / MCA', to: '/courses/bca' },
          { label: 'AI & Machine Learning', to: '/courses/ai-ml' },
          { label: 'Diploma Engineering', to: '/courses/diploma-engg' },
          { label: 'B.Sc Computer Science', to: '/courses/bsc-cs' },
        ],
      },
      {
        title: 'Business, Nursing & Law',
        icon: '📊',
        links: [
          { label: 'MBA Programs', to: '/courses/mba-india' },
          { label: 'BBA – Business Administration', to: '/courses/bba' },
          { label: 'B.Sc Nursing', to: '/courses/bsc-nursing' },
          { label: 'GNM Nursing', to: '/courses/gnm' },
          { label: 'ANM Nursing', to: '/courses/anm' },
          { label: 'BA LLB / BBA LLB', to: '/courses/ba-llb' },
        ],
      },
    ],
    cta: { label: 'View All Courses →', to: '/courses' },
  },
  'Study Abroad': {
    sections: [
      {
        title: 'By Country',
        icon: '🌍',
        links: [
          { label: '🇺🇸 Study in USA', to: '/country/usa' },
          { label: '🇬🇧 Study in UK', to: '/country/uk' },
          { label: '🇨🇦 Study in Canada', to: '/country/canada' },
          { label: '🇦🇺 Study in Australia', to: '/country/australia' },
          { label: '🇩🇪 Study in Germany', to: '/country/germany' },
          { label: '🇸🇬 Study in Singapore', to: '/country/singapore' },
          { label: '🇮🇪 Study in Ireland', to: '/country/ireland' },
        ],
      },
      {
        title: 'By Program',
        icon: '🎓',
        links: [
          { label: 'MBBS Abroad', to: '/courses?category=medical' },
          { label: 'MBA Abroad', to: '/courses?category=business' },
          { label: 'Engineering Abroad', to: '/courses?category=engineering' },
          { label: 'Nursing Abroad', to: '/courses?category=nursing' },
          { label: 'IT Programs Abroad', to: '/courses?category=it' },
        ],
      },
    ],
    cta: { label: 'Explore All Destinations →', to: '/study-abroad' },
  },
  Services: {
    sections: [
      {
        title: 'Counseling Services',
        icon: '👨‍💼',
        links: [
          { label: 'Free Counseling Session', to: '/free-counseling' },
          { label: 'Medical Counseling (NEET)', to: '/medical-counseling' },
          { label: 'Engineering Counseling', to: '/engineering-counseling' },
          { label: 'Management Programs', to: '/management-programs' },
          { label: 'Nursing & Paramedical', to: '/nursing-programs' },
        ],
      },
      {
        title: 'Support Services',
        icon: '📋',
        links: [
          { label: '🤖 AI Career Finder', to: '/ai-recommendation' },
          { label: 'Scholarship Guidance', to: '/scholarships' },
          { label: 'Visa Assistance', to: '/study-abroad' },
          { label: 'Document Support', to: '/free-counseling' },
          { label: 'University Application', to: '/universities' },
          { label: 'Career Guidance', to: '/free-counseling' },
        ],
      },
    ],
    cta: { label: 'Book Free Consultation →', to: '/free-counseling' },
  },
};

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveMenu(null);
  }, [location]);

  // key = used for mega-menu lookup (always English), label = displayed text
  const navLinks = [
    { key: 'Home', label: t('nav.home'), to: '/' },
    { key: 'About', label: t('nav.about'), to: '/about' },
    { key: 'Courses', label: t('nav.courses'), hasMega: true },
    { key: 'Study Abroad', label: t('nav.studyAbroad'), hasMega: true },
    { key: 'Universities', label: t('nav.universities'), to: '/universities' },
    { key: 'Services', label: t('nav.services'), hasMega: true },
    { key: 'Scholarships', label: t('nav.scholarships'), to: '/scholarships' },
    { key: 'Blog', label: t('nav.blog'), to: '/blog' },
    { key: 'Contact', label: t('nav.contact'), to: '/contact' },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="bg-navy-900 text-white text-xs py-2 hidden md:block">
        <div className="container-custom flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a href="tel:+918310408977" className="flex items-center gap-1.5 hover:text-gold-400 transition-colors">
              <FiPhone size={12} /> +91 83104 08977
            </a>
            <a href="mailto:youthinfotech01@gmail.com" className="flex items-center gap-1.5 hover:text-gold-400 transition-colors">
              <FiMail size={12} /> youthinfotech01@gmail.com
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <FiGlobe size={12} /> Global Education Consultancy
            </span>
            <span className="text-gold-400 font-semibold">5000+ Students Placed Worldwide</span>
            {/* Language Switcher - Top Bar */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-2.5 py-1 rounded-md text-xs font-semibold transition-all duration-200 border border-white/20"
              aria-label="Switch language"
            >
              {language === 'en' ? '🇮🇳 हिंदी' : '🇺🇸 English'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-white border-b border-gray-100'
      }`}>
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-18">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
              <div className="w-10 h-10 bg-hero-gradient rounded-xl flex items-center justify-center shadow-md">
                <span className="text-white font-heading font-bold text-lg">Y</span>
              </div>
              <div>
                <div className="font-heading font-bold text-navy-900 text-lg leading-none">YouthInfotech</div>
                <div className="text-xs text-gray-500 font-medium leading-none">Global Education Consultancy</div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) =>
                link.hasMega ? (
                  <div
                    key={link.key}
                    className="relative"
                    onMouseEnter={() => setActiveMenu(link.key)}
                    onMouseLeave={() => setActiveMenu(null)}
                  >
                    <button className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      activeMenu === link.key ? 'text-navy-800 bg-navy-50' : 'text-gray-700 hover:text-navy-800 hover:bg-gray-50'
                    }`}>
                      {link.label}
                      <FiChevronDown size={14} className={`transition-transform ${activeMenu === link.key ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Mega Menu Dropdown */}
                    {activeMenu === link.key && megaMenuItems[link.key] && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 z-50"
                        style={{ minWidth: '600px' }}
                      >
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                          {megaMenuItems[link.key].sections.map((section) => (
                            <div key={section.title}>
                              <div className="text-xs font-bold text-navy-900 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                <span>{section.icon}</span> {section.title}
                              </div>
                              <ul className="space-y-1.5">
                                {section.links.map((item) => (
                                  <li key={item.label}>
                                    <Link
                                      to={item.to}
                                      className="text-sm text-gray-600 hover:text-navy-800 hover:bg-navy-50 px-2 py-1 rounded-lg block transition-colors"
                                    >
                                      {item.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <Link
                            to={megaMenuItems[link.key].cta.to}
                            className="text-sm font-semibold text-navy-700 hover:text-navy-900 transition-colors"
                          >
                            {megaMenuItems[link.key].cta.label}
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.key}
                    to={link.to}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      location.pathname === link.to
                        ? 'text-navy-800 bg-navy-50 font-semibold'
                        : 'text-gray-700 hover:text-navy-800 hover:bg-gray-50'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </div>

            {/* CTA Button + Language */}
            <div className="hidden lg:flex items-center gap-3">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1.5 border border-gray-200 hover:border-navy-300 text-gray-600 hover:text-navy-800 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                aria-label="Switch language"
              >
                <FiGlobe size={14} />
                {language === 'en' ? 'हिंदी' : 'English'}
              </button>
              <Link to="/free-counseling" className="btn-primary text-sm px-5 py-2.5">
                {t('nav.bookSession')}
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <FiX size={22} /> : <FiMenu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 max-h-[80vh] overflow-y-auto">
            <div className="container-custom py-4 space-y-1">
              {navLinks.map((link) =>
                link.hasMega ? (
                  <div key={link.key}>
                    <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider mt-3">
                      {link.label}
                    </div>
                    {megaMenuItems[link.key]?.sections.map((section) =>
                      section.links.map((item) => (
                        <Link
                          key={item.label}
                          to={item.to}
                          className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-navy-50 hover:text-navy-800 rounded-lg transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.key}
                    to={link.to}
                    className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-navy-50 hover:text-navy-800 rounded-lg transition-colors"
                  >
                    {link.label}
                  </Link>
                )
              )}
              <div className="pt-4 border-t border-gray-100 mt-4 flex gap-2">
                <button
                  onClick={toggleLanguage}
                  className="flex-1 flex items-center justify-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2.5 rounded-xl text-sm font-medium"
                >
                  <FiGlobe size={14} />
                  {language === 'en' ? 'हिंदी' : 'English'}
                </button>
                <Link to="/free-counseling" className="btn-primary flex-1 justify-center text-sm py-2.5">
                  {t('nav.bookSession')}
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
